import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  BugReport as BugReportIcon,
  Palette as FigmaIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import { SV } from '../automation/stratovaiTestIds';

const ProductKnowledgeRowActions = ({
  item,
  hasGenerated,
  isGenerating,
  canGenerateTestCases = true,
  onGenerate,
  onRegenerate,
  onPreview,
  onDelete,
  onViewImpactedAreas,
  onAnalyzeRequirement,
  onGenerateRCA
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);

  const open = Boolean(anchorEl);
  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handlePreview = () => {
    handleClose();
    onPreview(item);
  };

  const handleGenerate = () => {
    handleClose();
    onGenerate(item, false);
  };

  const handleRegenerateClick = () => {
    handleClose();
    setRegenerateConfirmOpen(true);
  };

  const handleRegenerateConfirm = () => {
    onRegenerate(item);
    setRegenerateConfirmOpen(false);
  };

  const handleViewImpactedAreas = () => {
    handleClose();
    onViewImpactedAreas(item);
  };

  const handleAnalyzeRequirement = () => {
    handleClose();
    if (onAnalyzeRequirement) onAnalyzeRequirement(item);
  };

  const handleReimport = () => {
    handleClose();
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('pkb-reimport-request', { detail: { item } });
      window.dispatchEvent(event);
    }
  };

  const handleFigmaReimport = () => {
    handleClose();
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('pkb-figma-reimport-request', { detail: { item } });
      window.dispatchEvent(event);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        data-testid={SV.pk.rowActionsTrigger}
        onClick={handleOpen}
        disabled={isGenerating}
        sx={{ minWidth: 'auto', px: 2, fontSize: '0.75rem' }}
      >
        {isGenerating ? 'Working...' : 'Actions'}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'data-testid': SV.pk.rowActionsMenu }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem data-testid={SV.pk.menuViewItem} onClick={handlePreview} disabled={isGenerating}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View item" />
        </MenuItem>

        {/* Re-Import from JIRA (only for JIRA-sourced docs) */}
        {item?.source === 'jira' && (
          <MenuItem data-testid={SV.pk.menuJiraReimport} onClick={handleReimport} disabled={isGenerating}>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Re-Import from JIRA" />
          </MenuItem>
        )}

        {/* Re-Import from Figma (only for direct Figma-sourced docs with stored ingested_url) */}
        {item?.source === 'figma' && item?.content?.figma?.ingested_url && (
          <MenuItem data-testid={SV.pk.menuFigmaReimport} onClick={handleFigmaReimport} disabled={isGenerating}>
            <ListItemIcon>
              <FigmaIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Re-Import from Figma" />
          </MenuItem>
        )}

        {onAnalyzeRequirement && (
          <MenuItem
            data-testid={SV.pk.menuAnalyzeRequirement}
            onClick={handleAnalyzeRequirement}
            disabled={isGenerating}
          >
            <ListItemIcon>
              <FactCheckIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Analyze Requirement" />
          </MenuItem>
        )}

        <MenuItem data-testid={SV.pk.menuImpactedAreas} onClick={handleViewImpactedAreas} disabled={isGenerating}>
          <ListItemIcon>
            <AssessmentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View Impacted Areas" />
        </MenuItem>

        {/* Generate RCA - only for Bug issue types */}
        {(() => {
          const issueType = item?.content?.metadata?.issue_type?.toLowerCase() || 
                          item?.content?.metadata?.knowledge_type?.toLowerCase() || '';
          const isBug = issueType === 'bug';
          
          if (isBug && onGenerateRCA) {
            return (
              <MenuItem 
                onClick={() => {
                  handleClose();
                  onGenerateRCA(item);
                }} 
                disabled={isGenerating}
              >
                <ListItemIcon>
                  <BugReportIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Generate RCA" />
              </MenuItem>
            );
          }
          return null;
        })()}

        <Divider />

        {!hasGenerated && canGenerateTestCases && (
          <MenuItem data-testid={SV.pk.menuGenerateTestCases} onClick={handleGenerate} disabled={isGenerating}>
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Generate Test Cases" />
          </MenuItem>
        )}

        {hasGenerated && canGenerateTestCases && (
          <MenuItem data-testid={SV.pk.menuRegenerateTestCases} onClick={handleRegenerateClick} disabled={isGenerating}>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Regenerate Test Cases..." />
          </MenuItem>
        )}
      </Menu>

      {/* Regenerate confirmation dialog */}
      <Dialog open={regenerateConfirmOpen} onClose={() => setRegenerateConfirmOpen(false)} PaperProps={{ 'data-testid': SV.pk.regenerateTcConfirm }}>
        <DialogTitle>Regenerate Test Cases</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will generate new test cases from the PRD content using the full PKB knowledge base. 
            This may create duplicates of existing test cases. 
            Are you sure you want to regenerate test cases for "{item?.content?.name || 'this document'}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateConfirmOpen(false)}>Cancel</Button>
          <Button 
            variant="contained"
            data-testid={SV.pk.regenerateTcConfirmAction}
            onClick={handleRegenerateConfirm}
            disabled={isGenerating}
          >
            {isGenerating ? 'Working...' : 'Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProductKnowledgeRowActions;
